import { MgnlContent } from '../types/MgnlContent';
import { MgnlTemplateAnnotations } from '../types/MgnlTemplateAnnotations';
import { EditorContextService } from './EditorContextService';
import * as LoggerService from './LoggerService';
import { generateMissingAreas } from './TemplateAnnotations';

export const getSelectedVariant = (
  content?: MgnlContent | null,
  templateAnnotations?: MgnlTemplateAnnotations | null
): MgnlContent | null | undefined => {
  if (!content || !templateAnnotations) {
    return content;
  }
  const annotations = templateAnnotations[content?.['@path']];
  const match = annotations
    ? annotations.match(/selectedVariant="(.+)"/)
    : null;
  if (!match) return content;
  const variant = match[1];

  if (variant === content['@name']) {
    return content;
  }

  return content[variant] ? (content[variant] as MgnlContent) : null;
};

export const getVariant = (
  content?: MgnlContent | null,
  templateAnnotations?: MgnlTemplateAnnotations | null
): MgnlContent | null | undefined => {
  if (
    !EditorContextService.inIframe() ||
    EditorContextService.inPreviewAsVisitor()
  ) {
    return content;
  }

  return getSelectedVariant(content, templateAnnotations);
};

/**
 * Wrap template annotations in order to resolve personalized content.
 *
 * Search for 'selectedVariant' in the template annotations and try to replace the value
 * of the aforementioned attribute with selected one by the variant select.
 *
 * Server does not know which variant was selected, thus we store it in the client app session on
 * variant select change.
 */
export const wrap = (
  templateAnnotations?: MgnlTemplateAnnotations | null,
  selectedComponentVariants?: MgnlTemplateAnnotations | null
): MgnlTemplateAnnotations | null | undefined => {
  if (!templateAnnotations || !selectedComponentVariants) {
    return templateAnnotations;
  }
  const modified: MgnlTemplateAnnotations = {};
  const selectedVariantRegex = /selectedVariant="(.+?)"/;
  const contentAttributeRegex = /content="(.+?)"/;
  const variantsAttributesRegex = /((variants|variantTitles)=".+?")/g;
  Object.entries(templateAnnotations).forEach(entry => {
    const [path, comment] = entry;
    if (
      selectedVariantRegex.test(comment) &&
      path in selectedComponentVariants
    ) {
      let selectedVariant: string | undefined = selectedComponentVariants[path];
      // name of the 'original' variant has to be remapped to the node name
      // get it from the path
      if (selectedVariant === 'original') {
        selectedVariant = path.split('/').pop();
        modified[path] = comment.replace(
          selectedVariantRegex,
          `selectedVariant="${selectedVariant}"`
        );
      } else {
        // in case of variant, use the variant's template annotation and replace content and variants from original
        const variantPath = path + '/variants/' + selectedVariant;
        const variant = templateAnnotations[variantPath];
        if (variant != null) {
          modified[path] = variant.replace(
            contentAttributeRegex,
            comment.match(contentAttributeRegex)?.[0] || ''
          );
          modified[path] = modified[path].concat(
            ' ',
            comment.match(variantsAttributesRegex)?.join(' ') || ''
          );
          modified[path] = modified[path].concat(
            ' ',
            `selectedVariant="${selectedVariant}"`
          );
        } else {
          LoggerService.warn(
            "Cannot find the variant with path '%s'. Please review the document https://docs.magnolia-cms.com/headless/spa-development/personalization-of-headless-SPA-projects.html#_notes.",
            variantPath
          );
        }
      }
    }
  });
  // merge the template annotations with modified annotations
  return { ...templateAnnotations, ...modified };
};

/**
 * Generate areas if missing from template annotations, and select component variant from selected component variants
 *
 * @param originalContent original content form Magnolia
 * @param templateAnnotations original template annotations from Magnolia
 * @param selectedComponentVariants selected variants from the Page Editor
 * @returns { Content, AnnotationMap }
 */
export const getRenderContent = (
  originalContent?: MgnlContent | null,
  templateAnnotations?: MgnlTemplateAnnotations | null,
  selectedComponentVariants?: MgnlTemplateAnnotations | null
): {
  content?: MgnlContent | null;
  templateAnnotations?: MgnlTemplateAnnotations | null;
} => {
  let mergedTemplateAnnotations;
  if (!templateAnnotations || !selectedComponentVariants) {
    mergedTemplateAnnotations = templateAnnotations || {};
  } else {
    mergedTemplateAnnotations = wrap(
      templateAnnotations,
      selectedComponentVariants
    );
  }

  let content = generateMissingAreas(
    originalContent,
    mergedTemplateAnnotations
  );

  content = getSelectedVariant(content, mergedTemplateAnnotations);
  return {
    content,
    templateAnnotations: mergedTemplateAnnotations,
  };
};
